import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw, Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

type SavedCard = { id: string; brand: string; last4: string; expMonth: number; expYear: number };

function cardLabel(c: SavedCard) {
  return `${c.brand.charAt(0).toUpperCase() + c.brand.slice(1)} ····${c.last4} ${c.expMonth}/${String(c.expYear).slice(-2)}`;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function AiAutoReloadCard({ workspaceId }: { workspaceId?: string }) {
  const { toast } = useToast();

  const { data: cfg, refetch } = useQuery<{
    enabled: boolean; threshold_cents: number; pack_cents: number;
    stripe_payment_method_id: string | null; last_triggered_at: string | null;
  }>({
    queryKey: ["/api/ai-credits/auto-reload", workspaceId],
    queryFn: () => fetch("/api/ai-credits/auto-reload").then((r) => r.json()),
  });

  const { data: packs } = useQuery<Array<{ priceCents: number; creditsCents: number; label: string }>>({
    queryKey: ["/api/ai-credits/packs"],
    queryFn: () => fetch("/api/ai-credits/packs").then((r) => r.json()),
    staleTime: Infinity,
  });

  const { data: pmData } = useQuery<{ methods: SavedCard[] }>({
    queryKey: ["/api/billing/payment-methods", workspaceId],
    queryFn: () => fetch("/api/billing/payment-methods").then((r) => r.json()),
  });

  const { data: balData } = useQuery<{ balanceCents: number }>({
    queryKey: ["/api/ai-credits/balance", workspaceId],
    queryFn: () => fetch("/api/ai-credits/balance").then((r) => r.json()),
  });

  const [enabled, setEnabled] = useState(false);
  const [threshold, setThreshold] = useState("500");
  const [pack, setPack] = useState("2500");
  const [pmId, setPmId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!cfg) return;
    setEnabled(cfg.enabled);
    setThreshold(String(cfg.threshold_cents ?? 500));
    setPack(String(cfg.pack_cents ?? 2500));
    setPmId(cfg.stripe_payment_method_id ?? "");
  }, [cfg]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await apiRequest("PATCH", "/api/ai-credits/auto-reload", {
        enabled, thresholdCents: Number(threshold), packCents: Number(pack),
        stripePaymentMethodId: pmId || null,
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message || "Save failed");
      toast({ title: "AI auto-reload saved" });
      refetch();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const cards = pmData?.methods ?? [];
  const lowBalance = (balData?.balanceCents ?? 0) < 200 && !cfg?.enabled;

  return (
    <Card data-testid="card-ai-auto-reload">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-muted-foreground" />
            <div>
              <CardTitle className="text-base">AI Credit Auto-Reload</CardTitle>
              <CardDescription>Automatically top up when your AI balance runs low.</CardDescription>
            </div>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="switch-ai-auto-reload" aria-label="Enable AI credit auto-reload" />
        </div>
      </CardHeader>
      {lowBalance && (
        <div className="px-6 pb-2">
          <Alert variant="destructive" className="py-2">
            <AlertDescription className="text-xs">
              AI credit balance is low. Enable auto-reload to avoid interruptions.
            </AlertDescription>
          </Alert>
        </div>
      )}
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Reload when balance below</Label>
            <Select value={threshold} onValueChange={setThreshold} disabled={!enabled}>
              <SelectTrigger data-testid="select-ai-threshold"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="200">20 cr</SelectItem>
                <SelectItem value="500">50 cr</SelectItem>
                <SelectItem value="1000">100 cr</SelectItem>
                <SelectItem value="2000">200 cr</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Reload with pack</Label>
            <Select value={pack} onValueChange={setPack} disabled={!enabled}>
              <SelectTrigger data-testid="select-ai-pack"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(packs ?? []).map((p) => (
                  <SelectItem key={p.priceCents} value={String(p.priceCents)}>
                    {p.label} — {(p.creditsCents / 10).toLocaleString()} cr · ${(p.priceCents / 100).toFixed(0)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Charge card</Label>
          {cards.length === 0 ? (
            <p className="text-xs text-muted-foreground">No saved card on file. Complete billing setup to add one.</p>
          ) : (
            <Select value={pmId} onValueChange={setPmId} disabled={!enabled}>
              <SelectTrigger data-testid="select-ai-pm"><SelectValue placeholder="Select a card" /></SelectTrigger>
              <SelectContent>
                {cards.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{cardLabel(c)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        {cfg?.last_triggered_at && (
          <p className="text-xs text-muted-foreground">Last reloaded: {formatDate(cfg.last_triggered_at)}</p>
        )}
        <Button size="sm" onClick={handleSave} disabled={saving} data-testid="button-save-ai-auto-reload">
          {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Saving…</> : "Save changes"}
        </Button>
      </CardContent>
    </Card>
  );
}
